// src/composables/useDevices.js
// Device management API composable

import { ref } from 'vue';
import { useFetch } from '@/plugins/fetch';

export function useDevices() {
  const fetch = useFetch();
  const isLoading = ref(false);
  const error = ref(null);

  const extractArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
      const keys = ['devices', 'sensor_types', 'results', 'data', 'pins'];
      for (const key of keys) {
        if (Array.isArray(payload[key])) return payload[key];
        if (payload[key] && typeof payload[key] === 'object' && Array.isArray(payload[key].data)) {
          return payload[key].data;
        }
      }
    }
    return [];
  };

  const normalizeDevice = (device) => {
    if (!device) return device;

    const base = device.device && typeof device.device === 'object' ? device.device : device;
    const id = base.id ?? base.device_id ?? base.deviceId ?? device.id ?? device.device_id ?? device.deviceId ?? device?.device?.id;
    const merged = { ...device, ...base };
    delete merged.device;

    return {
      ...merged,
      id,
      status: (device.status ?? base.status ?? '').toLowerCase(),
      device_type_label: device.device_type_label || device.device_type_display || base.device_type_label || base.device_type_display || base.device_type,
      last_seen: device.last_seen || device.last_seen_at || base.last_seen || base.last_seen_at,
      pins_preview: device.pins_preview || base.pins_preview || device.pins || base.pins || [],
      wifi_ssid: device.wifi_ssid ?? device.network_ssid ?? base.wifi_ssid ?? base.network_ssid ?? null,
      wifi_rssi: device.wifi_rssi ?? base.wifi_rssi ?? null,
    };
  };

  // List all devices
  const getDevices = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch('devices/');
      const payload = response?.data ?? response ?? [];
      const devices = extractArray(payload);
      return devices.map(normalizeDevice);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get single device
  const getDevice = async (id) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${id}/`);
      const payload = response?.data || response;
      return normalizeDevice(payload);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Provision new device
  const provisionDevice = async (deviceData) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch('devices/', {
        method: 'POST',
        body: deviceData,
      });
      const payload = normalizeDevice(response?.data || response);
      // Normalize pins_preview if backend supplies pins on provision
      if (payload && Array.isArray(payload.pins)) {
        payload.pins_preview = payload.pins.slice(0, 6);
      }
      return payload;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Update device
  const updateDevice = async (id, deviceData) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${id}/`, {
        method: 'PUT',
        body: deviceData,
      });
      return response?.data || response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Delete device
  const deleteDevice = async (id) => {
    isLoading.value = true;
    error.value = null;
    try {
      await fetch(`devices/${id}/`, { method: 'DELETE' });
      return true;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get device pins
  const getDevicePins = async (deviceId) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${deviceId}/pins/`);
      return response?.data || response || [];
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Configure a pin
  const configurePin = async (deviceId, pinId, pinConfig) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${deviceId}/pins/${pinId}/`, {
        method: 'PUT',
        body: pinConfig,
      });
      return response?.data || response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Create sensor from pin
  const createSensorFromPin = async (deviceId, pinId, sensorData) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${deviceId}/pins/${pinId}/create-sensor/`, {
        method: 'POST',
        body: sensorData,
      });
      return response?.data || response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Send config command to device
  const sendConfigCommand = async (deviceId) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${deviceId}/commands/config/`, {
        method: 'POST',
      });
      return response?.data || response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Reboot device
  const rebootDevice = async (deviceId) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${deviceId}/commands/reboot/`, {
        method: 'POST',
      });
      return response?.data || response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Identify device (flash LED)
  const identifyDevice = async (deviceId) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`devices/${deviceId}/commands/identify/`, {
        method: 'POST',
      });
      return response?.data || response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get available device types
  const getDeviceTypes = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch('devices/types/');
      return response?.data || response || [];
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get available sensor types
  const getSensorTypes = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch('devices/sensor-types/');
      const payload = response?.data ?? response ?? [];
      const rawTypes = extractArray(payload);
      return rawTypes.map((t) => {
        if (typeof t === 'string') return { label: t, value: t };
        const label = t.label ?? t.name ?? t.value ?? t.type ?? String(t);
        const value = t.value ?? t.name ?? t.type ?? t.label ?? t;
        return { label, value };
      });
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    getDevices,
    getDevice,
    provisionDevice,
    updateDevice,
    deleteDevice,
    getDevicePins,
    configurePin,
    createSensorFromPin,
    sendConfigCommand,
    rebootDevice,
    identifyDevice,
    getDeviceTypes,
    getSensorTypes,
  };
}
